import React from 'react';
import clsx from 'clsx';

function getScoreColor(score) {
  if (score >= 80) return { stroke: '#10B981', text: 'text-success-500', label: 'Excellent' };
  if (score >= 60) return { stroke: '#14B8A6', text: 'text-secondary-500', label: 'Good' };
  if (score >= 40) return { stroke: '#F59E0B', text: 'text-accent-500', label: 'Average' };
  if (score >= 20) return { stroke: '#F97316', text: 'text-warning-500', label: 'Low' };
  return { stroke: '#F43F5E', text: 'text-error-500', label: 'Poor' };
}

export default function ScoreGauge({ score = 0, size = 120, label, className }) {
  const clamped = Math.min(100, Math.max(0, score));
  const { stroke, text, label: autoLabel } = getScoreColor(clamped);

  // SVG ring geometry
  const radius = (size - 16) / 2;          // 8px stroke clearance on each side
  const circumference = 2 * Math.PI * radius;
  const progress = circumference - (clamped / 100) * circumference;

  const center = size / 2;
  const strokeWidth = size >= 100 ? 8 : 6;

  return (
    <div
      className={clsx('inline-flex flex-col items-center gap-2', className)}
      aria-label={`Score: ${clamped} out of 100`}
    >
      <div className="relative" style={{ width: size, height: size }}>
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          className="-rotate-90"
          aria-hidden="true"
        >
          {/* Background track */}
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke="#E2E8F0"
            strokeWidth={strokeWidth}
          />
          {/* Progress arc */}
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke={stroke}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={progress}
            style={{ transition: 'stroke-dashoffset 0.6s ease-out, stroke 0.4s ease' }}
          />
        </svg>

        {/* Score number overlay */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span
            className={clsx('font-heading font-bold leading-none tabular-nums', text)}
            style={{ fontSize: size >= 100 ? size * 0.22 : size * 0.26 }}
          >
            {clamped}
          </span>
          <span className="mt-0.5 text-xs font-medium text-neutral-400">/ 100</span>
        </div>
      </div>

      {/* Label */}
      {(label || autoLabel) && (
        <span className={clsx('text-sm font-semibold', text)}>
          {label ?? autoLabel}
        </span>
      )}
    </div>
  );
}
